import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Upload, FolderOpen, Download, Eye, Trash2, Share2, Lock, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { escapeDeep } from "@/lib/utils";
import axios from "axios";
import { can } from "@/lib/permissions";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOLDERS = [
  { key: "all", label: "All Files" },
  { key: "engineering_drawings", label: "Engineering Drawings" },
  { key: "approvals", label: "Approvals & NOCs" },
  { key: "procurement", label: "Procurement (POs)" },
  { key: "invoices", label: "Invoices (PIs)" },
  { key: "site_photos", label: "Site Photos" },
  { key: "test_reports", label: "Test Reports" },
  { key: "handover", label: "Handover Documents" },
];

const getFileIcon = (fileType) => {
  if (!fileType) return "📁";
  if (fileType.startsWith("image/")) return "🖼";
  if (fileType === "application/pdf") return "📄";
  if (fileType.includes("spreadsheet") || fileType.includes("excel")) return "📊";
  if (fileType.includes("word") || fileType.includes("document")) return "📝";
  if (fileType === "financial/po") return "📋";
  if (fileType === "financial/pi") return "🧾";
  return "📁";
};

export default function DocumentVaultPage() {
  const { id: projectId } = useParams();
  const { user, workspace } = useAuth();
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadForm, setUploadForm] = useState({ name: "", folder: "engineering_drawings", is_shared_with_client: false });
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const canUpload = can(user, "documents.edit");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, dRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`),
          axios.get(`${API}/documents?project_id=${projectId}`)
        ]);
        setProject(pRes.data);
        setDocs(dRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [projectId]);

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("project_id", projectId);
      fd.append("name", uploadForm.name || file.name);
      fd.append("folder", uploadForm.folder);
      fd.append("is_shared_with_client", uploadForm.is_shared_with_client);
      fd.append("file", file);
      const { data } = await axios.post(`${API}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setDocs(p => [data, ...p]);
      setShowUpload(false);
      setFile(null);
      setUploadForm({ name: "", folder: "engineering_drawings", is_shared_with_client: false });
      toast.success("Document uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/documents/${docId}`);
      setDocs(p => p.filter(d => d.id !== docId));
      toast.success("Document deleted");
    } catch (err) { console.error(err); }
  };

  const toggleShare = async (doc) => {
    try {
      const newShared = !doc.is_shared_with_client;
      await axios.put(`${API}/documents/${doc.id}`, { is_shared_with_client: newShared });
      setDocs(p => p.map(d => d.id === doc.id ? { ...d, is_shared_with_client: newShared } : d));
      if (newShared && !can(user, "projects.edit")) {
        toast.success("Document visible to the client on the portal");
        return;
      }
      if (newShared) {
        let portalLink = "";
        try {
          const { data: sl } = await axios.get(`${API}/projects/${doc.project_id}/share-link`);
          portalLink = sl.url || `${window.location.origin}/client/portal/${sl.share_token}`;
        } catch { toast.success("Document visible to the client on the portal"); return; }
        const fallback = (text) => {
          const el = document.createElement("textarea");
          el.value = text; el.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
          document.body.appendChild(el); el.focus(); el.select();
          document.execCommand("copy"); document.body.removeChild(el);
        };
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(portalLink).catch(() => fallback(portalLink));
        } else {
          fallback(portalLink);
        }
        toast.success("Document visible to client. Portal link copied!", { description: portalLink, duration: 5000 });
      } else {
        toast.success("Marked as internal — hidden from client portal");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update sharing"); }
  };

  const viewDoc = async (doc) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const isFinancial = doc.file_type?.startsWith("financial/");
    const isImage = doc.file_type?.startsWith("image/");

    if (isFinancial) {
      try {
        const docType = doc.file_type.replace("financial/", "");
        const urlParts = doc.file_url.split("/");
        const docId = urlParts[urlParts.length - 1];
        const { data: rawFdoc } = await axios.get(`${API}/financials/${docType}/${docId}`);
        const fdoc = escapeDeep(rawFdoc);            // never interpolate raw user text into HTML
        const ws = escapeDeep(workspace || {});
        const docTypeName = docType === "po" ? "PURCHASE ORDER" : "PROFORMA INVOICE";
        const numberField = docType === "po" ? fdoc.po_number : fdoc.pi_number;

        const itemsRows = (fdoc.items || []).map((item, i) => `
          <tr style="background:${i % 2 === 0 ? "#fff" : "#f8fafc"}">
            <td style="padding:6px 8px;text-align:center">${i + 1}</td>
            <td style="padding:6px 8px">${item.description || ""}</td>
            <td style="padding:6px 8px;text-align:center">${item.hsn_code || ""}</td>
            <td style="padding:6px 8px;text-align:center">${item.quantity}</td>
            <td style="padding:6px 8px;text-align:center">${item.unit}</td>
            <td style="padding:6px 8px;text-align:right">${(item.rate || 0).toLocaleString("en-IN", {minimumFractionDigits:2})}</td>
            <td style="padding:6px 8px;text-align:right;font-weight:600">${(item.amount || 0).toLocaleString("en-IN", {minimumFractionDigits:2})}</td>
          </tr>`).join("");

        const taxRows = [
          fdoc.cgst > 0 ? `<tr><td colspan="5"></td><td style="padding:4px 8px;text-align:right">CGST @ ${fdoc.cgst_rate}%</td><td style="padding:4px 8px;text-align:right">₹ ${(fdoc.cgst||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td></tr>` : "",
          fdoc.sgst > 0 ? `<tr><td colspan="5"></td><td style="padding:4px 8px;text-align:right">SGST @ ${fdoc.sgst_rate}%</td><td style="padding:4px 8px;text-align:right">₹ ${(fdoc.sgst||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td></tr>` : "",
          fdoc.igst > 0 ? `<tr><td colspan="5"></td><td style="padding:4px 8px;text-align:right">IGST @ ${fdoc.igst_rate}%</td><td style="padding:4px 8px;text-align:right">₹ ${(fdoc.igst||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td></tr>` : "",
          (docType === "pi" && fdoc.tds_amount > 0) ? `<tr><td colspan="5"></td><td style="padding:4px 8px;text-align:right;color:#dc2626">TDS @ ${fdoc.tds_rate}%</td><td style="padding:4px 8px;text-align:right;color:#dc2626">- ₹ ${(fdoc.tds_amount||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td></tr>` : "",
        ].join("");

        const rightCol = docType === "po"
          ? `<div style="font-size:11px;color:#475569"><strong>Vendor:</strong> ${fdoc.vendor_name || ""}<br>${fdoc.vendor_address ? fdoc.vendor_address.replace(/\n/g,"<br>") : ""}<br>${fdoc.vendor_gstin ? `<strong>GSTIN:</strong> ${fdoc.vendor_gstin}` : ""}</div>`
          : `<div style="font-size:11px;color:#475569"><strong>Bill To:</strong><br>${(fdoc.bill_to || "").replace(/\n/g,"<br>")}<br><br><strong>Milestone:</strong> ${fdoc.milestone || "N/A"}</div>`;

        const bankSection = ws.bank_name ? `
          <div style="margin-top:24px;padding:12px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;font-size:11px">
            <strong>Bank Details:</strong><br>
            Bank: ${ws.bank_name} &nbsp;|&nbsp; A/C No: ${ws.bank_account || ""} &nbsp;|&nbsp; IFSC: ${ws.bank_ifsc || ""} &nbsp;|&nbsp; Branch: ${ws.bank_branch || ""}
          </div>` : "";

        const html = `<!DOCTYPE html><html><head>
          <meta charset="UTF-8">
          <title>${docTypeName} - ${numberField}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; font-size:12px; color:#1e293b; background:#fff; }
            .page { max-width:800px; margin:0 auto; padding:32px; }
            table { width:100%; border-collapse:collapse; }
            @media print { body { padding:0; } .no-print { display:none; } .page { padding:16px; max-width:100%; } @page { margin:15mm; } }
          </style>
        </head><body>
          <div class="page">
            <div class="no-print" style="margin-bottom:20px;text-align:right">
              <button onclick="window.print()" style="background:#FFB703;border:none;padding:8px 20px;font-size:13px;font-weight:600;cursor:pointer;border-radius:4px">Print / Save as PDF</button>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:16px;background:#0A1128;color:white;border-radius:6px 6px 0 0">
              <div style="display:flex;align-items:center;gap:12px">
                ${ws.logo_url ? `<img src="${process.env.REACT_APP_BACKEND_URL}${ws.logo_url}" alt="logo" style="height:44px;width:auto;object-fit:contain;background:white;padding:3px;border-radius:3px">` : ""}
                <div>
                  <div style="font-size:18px;font-weight:700">${ws.name || "Company Name"}</div>
                  ${ws.gstin ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px">GSTIN: ${ws.gstin}</div>` : ""}
                  ${ws.address ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px">${ws.address}</div>` : ""}
                </div>
              </div>
              <div style="text-align:right">
                <div style="font-size:20px;font-weight:800;color:#FFB703">${docTypeName}</div>
                <div style="font-size:14px;font-weight:600;margin-top:4px">${numberField}</div>
                <div style="font-size:11px;color:#94a3b8;margin-top:2px">Date: ${fdoc.date || ""}</div>
              </div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #e2e8f0;border-top:3px solid #FFB703">
              <div style="padding:12px;border-right:1px solid #e2e8f0;font-size:11px;color:#475569">
                <strong style="display:block;margin-bottom:4px;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#94a3b8">From (Our Company)</strong>
                <strong>${ws.name || ""}</strong><br>
                ${ws.address ? ws.address.replace(/\n/g,"<br>") : ""}
                ${ws.gstin ? `<br>GSTIN: ${ws.gstin}` : ""}
              </div>
              <div style="padding:12px;font-size:11px;color:#475569">
                <strong style="display:block;margin-bottom:4px;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#94a3b8">${docType === "po" ? "To (Vendor)" : "To (Client)"}</strong>
                ${rightCol}
              </div>
            </div>
            <div style="margin-top:16px">
              <table style="border:1px solid #e2e8f0;font-size:11px">
                <thead>
                  <tr style="background:#0A1128;color:white">
                    <th style="padding:8px;text-align:center;width:40px">#</th>
                    <th style="padding:8px;text-align:left">Description</th>
                    <th style="padding:8px;text-align:center;width:60px">HSN</th>
                    <th style="padding:8px;text-align:center;width:50px">Qty</th>
                    <th style="padding:8px;text-align:center;width:50px">Unit</th>
                    <th style="padding:8px;text-align:right;width:80px">Rate (₹)</th>
                    <th style="padding:8px;text-align:right;width:90px">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemsRows}
                  <tr style="border-top:2px solid #e2e8f0">
                    <td colspan="5"></td>
                    <td style="padding:6px 8px;text-align:right;font-weight:600">Subtotal</td>
                    <td style="padding:6px 8px;text-align:right;font-weight:600">₹ ${(fdoc.subtotal||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
                  </tr>
                  ${taxRows}
                  <tr style="background:#fef3c7">
                    <td colspan="5"></td>
                    <td style="padding:8px;text-align:right;font-weight:700;font-size:13px">TOTAL</td>
                    <td style="padding:8px;text-align:right;font-weight:700;font-size:13px;color:#0A1128">₹ ${(fdoc.total||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style="margin-top:12px;padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:11px;font-style:italic;color:#475569">
              <strong>Amount in Words:</strong> ${fdoc.amount_in_words || ""}
            </div>
            ${bankSection}
          </div>
        </body></html>`;

        const blob = new Blob([html], { type: "text/html" });
        const blobUrl = URL.createObjectURL(blob);
        const win = window.open(blobUrl, "_blank");
        if (!win) toast.error("Please allow popups to view this document");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch {
        toast.error("Failed to load financial document");
      }
      return;
    }

    if (isImage) {
      setImagePreview({ name: doc.name, url: `${baseUrl}${doc.file_url}` });
      return;
    }

    window.open(`${baseUrl}${doc.file_url}`, "_blank");
  };

  const downloadDoc = (doc) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const a = document.createElement("a");
    a.href = `${baseUrl}${doc.file_url}`;
    a.download = doc.name;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const filtered = activeFolder === "all" ? docs : docs.filter(d => d.folder === activeFolder);

  if (loading) return <div className="p-8 text-[#64748B]">Loading documents...</div>;

  return (
    <div className="p-6 animate-fade-up">
      <div className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
        <Link to="/app/projects" className="hover:text-[#FFB703]">Projects</Link>
        <ChevronRight size={12} />
        <Link to={`/app/projects/${projectId}`} className="hover:text-[#FFB703]">{project?.name}</Link>
        <ChevronRight size={12} />
        <span className="text-[#0A1128] font-medium">Document Vault</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-xl text-[#0A1128]">Document Vault</h1>
        {canUpload && (
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98]" data-testid="upload-doc-btn">
            <Upload size={14} />Upload Document
          </button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Folder sidebar */}
        <div className="w-48 flex-shrink-0">
          <div className="bg-white border border-[#E2E8F0] rounded overflow-hidden">
            {FOLDERS.map(f => (
              <button
                key={f.key}
                onClick={() => setActiveFolder(f.key)}
                className={`w-full text-left px-4 py-2.5 text-xs font-medium border-b border-[#F1F5F9] transition-colors ${activeFolder === f.key ? "bg-[#FEF3C7] text-[#0A1128] border-l-2 border-l-[#FFB703]" : "text-[#64748B] hover:bg-[#F8FAFC]"}`}
              >
                {f.label}
                <span className="ml-1 text-[#94A3B8]">({f.key === "all" ? docs.length : docs.filter(d => d.folder === f.key).length})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Files grid */}
        <div className="flex-1">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center bg-white border border-[#E2E8F0] rounded">
              <FolderOpen size={32} className="text-[#94A3B8] mb-3" />
              <p className="text-sm text-[#64748B]">No documents in this folder yet.</p>
              {canUpload && <button onClick={() => setShowUpload(true)} className="mt-3 text-xs text-[#FFB703] hover:underline">Upload the first document</button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map(doc => {
                const icon = getFileIcon(doc.file_type);
                const sizeKB = doc.file_size ? (doc.file_size / 1024).toFixed(1) + " KB" : "";
                const isFinancial = doc.file_type?.startsWith("financial/");
                return (
                  <div key={doc.id} className="bg-white border border-[#E2E8F0] rounded p-4 hover:border-[#FFB703]/50 hover:shadow-sm transition-all" data-testid={`doc-${doc.id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="text-2xl flex-shrink-0">{icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#0A1128] truncate" title={doc.name}>{doc.name}</div>
                        <div className="text-[10px] text-[#64748B]">{sizeKB || (isFinancial ? "Financial Document" : "")}</div>
                        <div className={`mt-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded inline-block ${doc.is_shared_with_client ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {doc.is_shared_with_client ? "Shared" : "Internal"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 justify-end border-t border-[#F1F5F9] pt-2 mt-1">
                      <button
                        onClick={() => viewDoc(doc)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-[#F8FAFC] hover:bg-[#FEF3C7] text-[#64748B] hover:text-[#0A1128] transition-colors border border-[#E2E8F0]"
                        title="View"
                        data-testid={`view-doc-${doc.id}`}
                      >
                        <Eye size={11} />View
                      </button>
                      {!isFinancial && (
                        <button
                          onClick={() => downloadDoc(doc)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-[#F8FAFC] hover:bg-[#FEF3C7] text-[#64748B] hover:text-[#0A1128] transition-colors border border-[#E2E8F0]"
                          title="Download"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download size={11} />Download
                        </button>
                      )}
                      {canUpload && (
                        <>
                          <button onClick={() => toggleShare(doc)} className="p-1.5 rounded hover:bg-slate-50 text-[#64748B] hover:text-[#0A1128] transition-colors" title={doc.is_shared_with_client ? "Mark as internal" : "Share with client"} data-testid={`share-doc-${doc.id}`}>
                            {doc.is_shared_with_client ? <Lock size={11} /> : <Share2 size={11} />}
                          </button>
                          <button onClick={() => deleteDoc(doc.id)} className="p-1.5 rounded hover:bg-red-50 text-[#64748B] hover:text-red-600 transition-colors" title="Delete" data-testid={`delete-doc-${doc.id}`}>
                            <Trash2 size={11} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {imagePreview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setImagePreview(null)}>
          <div className="relative max-w-4xl max-h-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setImagePreview(null)} className="absolute -top-10 right-0 text-[#1B2B22] hover:text-[#FFB703] transition-colors">
              <X size={24} />
            </button>
            <p className="text-[#1B2B22] text-sm mb-2 text-center">{imagePreview.name}</p>
            <img src={imagePreview.url} alt={imagePreview.name} className="max-w-full max-h-[80vh] rounded shadow-2xl object-contain" />
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-[#0A1128]">Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={uploadDoc} className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">File *</label>
              <input type="file" required onChange={e => { setFile(e.target.files[0]); if (e.target.files[0] && !uploadForm.name) setUploadForm(p => ({ ...p, name: e.target.files[0].name })); }} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Document Name</label>
              <input type="text" value={uploadForm.name} onChange={e => setUploadForm(p => ({ ...p, name: e.target.value }))} placeholder="Auto-filled from filename" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Folder</label>
              <select value={uploadForm.folder} onChange={e => setUploadForm(p => ({ ...p, folder: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                {FOLDERS.slice(1).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="share" checked={uploadForm.is_shared_with_client} onChange={e => setUploadForm(p => ({ ...p, is_shared_with_client: e.target.checked }))} className="w-4 h-4" />
              <label htmlFor="share" className="text-sm text-[#64748B]">Share with client portal</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowUpload(false)} className="px-4 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={uploading} className="bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors disabled:opacity-60">
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
